import React from "react";

const GROUPS = [
  {
    label: "Compute",
    ids: [
      "57srcl8zcn7c", // Workers
      "vcpsk0w6g86k", // Workers Builds
    ],
  },
  {
    label: "Storage",
    ids: [
      "z0yd3sxqjqdm", // D1
      "hb7g5sq2zz0h", // R2
    ],
  },
  {
    label: "Hosting",
    ids: [
      "vgxj684rcw7t", // Pages
    ],
  },
  {
    label: "Networking & SSL",
    ids: [
      "dp8ppfycqxcs", // Authoritative DNS
      "7j656z7tqk7f", // DNS Updates
      "9p2qlpt19nqb", // SSL for SaaS
      "cghykwlwsmn5", // SSL Certificates
    ],
  },
];

interface CloudflareComponent {
  id: string;
  name: string;
  status: string;
}

const DOT_CLASS: Record<string, string> = {
  operational:          "bg-success",
  degraded_performance: "bg-warning",
  partial_outage:       "bg-warning",
  major_outage:         "bg-destructive",
  under_maintenance:    "bg-muted-foreground/60",
};

const STATUS_LABEL: Record<string, string> = {
  operational:          "Operational",
  degraded_performance: "Degraded",
  partial_outage:       "Partial outage",
  major_outage:         "Major outage",
  under_maintenance:    "Maintenance",
};

function ServicePill({ component }: { component: CloudflareComponent }) {
  const dot = DOT_CLASS[component.status] ?? "bg-muted-foreground/40";
  const ok = component.status === "operational";
  return (
    <div
      className={`flex items-center gap-2.5 rounded-md border px-3 py-2 text-sm ${
        ok
          ? "border-border bg-background"
          : component.status === "major_outage"
          ? "border-destructive/30 bg-destructive/5"
          : "border-warning-border bg-warning-tint"
      }`}
    >
      <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} />
      <span className="flex-1 font-medium text-foreground">{component.name}</span>
      {!ok && (
        <span className="text-[10px] font-medium text-muted-foreground">
          {STATUS_LABEL[component.status] ?? component.status}
        </span>
      )}
    </div>
  );
}

function SkeletonPill() {
  return (
    <div className="flex items-center gap-2.5 rounded-md border border-border px-3 py-2">
      <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-muted" />
      <span className="h-3 w-28 animate-pulse rounded bg-muted" />
    </div>
  );
}

export function CloudflareStatusDiagram({ components }: { components?: CloudflareComponent[] }) {
  const byId: Record<string, CloudflareComponent> = Object.fromEntries((components ?? []).map((c) => [c.id, c]));
  const loading = !components;

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <svg viewBox="0 0 40 40" className="h-4 w-4 shrink-0" aria-hidden="true">
          <path
            fill="#F6821F"
            d="M26.7 27.4c.3-.9.2-1.7-.2-2.3-.4-.5-1-.8-1.8-.9l-13.4-.2c-.1 0-.2-.1-.2-.2s.1-.2.2-.2l13.5-.2c1.6-.1 3.3-1.4 3.9-3l.8-2c0-.1 0-.2-.1-.2C28.3 14.6 24.4 12 20 12c-3.9 0-7.3 2.1-9.1 5.2-.8-.6-1.8-.9-2.9-.8-2 .2-3.6 1.8-3.8 3.9-.1.5 0 1 .1 1.5C2.3 22 1 23.4 1 25.1c0 1.7 1.4 3.1 3.1 3.1h22.1c.2 0 .4-.2.5-.4l-.1-.4z"
          />
          <path
            fill="#FBAD41"
            d="M30.3 19.5h-.5c-.1 0-.2-.1-.2-.2l-.3-1c-.5-1.9-2.3-3.3-4.3-3.3-.5 0-1 .1-1.4.3-.2.1-.4 0-.5-.2C22 13.4 20.1 12 17.9 12c-2.5 0-4.7 1.9-5 4.4 0 .2-.2.3-.3.3-1.6 0-2.9 1.3-2.9 2.9v.1l.1.1h20.5c.2 0 .3-.2.3-.3l-.3-.1v.1z"
          />
        </svg>
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Cloudflare
        </span>
      </div>
      <div className="grid grid-cols-2 gap-5 p-4 md:grid-cols-4">
        {GROUPS.map((group) => (
          <div key={group.label}>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {group.label}
            </p>
            <div className="flex flex-col gap-1.5">
              {loading
                ? group.ids.map((id) => <SkeletonPill key={id} />)
                : group.ids.map((id) => {
                    const comp = byId[id];
                    return comp ? <ServicePill key={id} component={comp} /> : null;
                  })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
